import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Layers, Package, Cylinder, ArrowRight } from "lucide-react";
import { Reveal, Eyebrow } from "../components/Reveal";
import { TUBE_ROUTES } from "../data/productPages";

const ICONS = { "butt-joint": Layers, telescopic: Cylinder, "tin-lid": Package };

export const Products = ({ products }) => (
  <section id="products" className="relative py-28 sm:py-36" data-testid="products-section">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <Reveal>
        <Eyebrow>Three constructions</Eyebrow>
        <h2 className="font-serif-display text-2xl sm:text-3xl lg:text-5xl font-medium tracking-tight text-[#F5F3EF] max-w-2xl leading-[1.15]">
          Choose the closure, the reveal<br className="hidden sm:block" /> and the <span className="foil-text italic">feel</span> your product deserves.
        </h2>
      </Reveal>
      <div className="mt-16 grid gap-6 md:grid-cols-3">
        {products.map((p, i) => {
          const Icon = ICONS[p.id] || Package;
          return (
            <Reveal key={p.id} delay={i * 0.12}>
              <article
                className="product-card group relative border border-white/8 bg-[#14171D] rounded-2xl overflow-hidden h-full flex flex-col"
                data-testid={`product-${p.id}-card`}
              >
                <div className="relative h-64 overflow-hidden bg-[#EFE7D8]">
                  <Link to={TUBE_ROUTES[p.id]} data-testid={`product-${p.id}-image-link`} aria-label={`View ${p.name} tubes`}>
                    <img src={p.image_url} alt={`${p.name} custom printed cardboard tube`} className="product-img w-full h-full object-contain p-3" />
                  </Link>
                  <div className="absolute inset-0 bg-gradient-to-t from-[#14171D] via-transparent to-transparent pointer-events-none" />
                </div>
                <div className="p-7 flex flex-col flex-1">
                  <div className="flex items-center justify-center gap-2 h-8 bg-[#0B0D10] border border-amber-500/20 rounded-full px-4 mb-5" data-testid={`product-${p.id}-tagline`}>
                    <Icon size={12} className="text-[#D4AF37] shrink-0" />
                    <span className="font-mono-spec text-[10px] uppercase tracking-[0.14em] text-[#D9A76A] whitespace-nowrap">{p.tagline}</span>
                  </div>
                  <h3 className="font-serif-display text-xl sm:text-2xl font-medium text-[#F5F3EF]">
                    <Link to={TUBE_ROUTES[p.id]} className="hover:text-[#E6CA65] transition-colors" data-testid={`product-${p.id}-title-link`}>{p.name}</Link>
                  </h3>
                  <p className="mt-3 text-sm text-[#A1A8B3] leading-relaxed flex-1">{p.description}</p>
                  <div className="mt-6 pt-5 border-t border-white/5">
                    <p className="font-mono-spec text-[9px] uppercase tracking-[0.25em] text-[#666F7D] mb-1.5">Best for</p>
                    <p className="text-[13px] text-[#D9A76A]">{p.best_for}</p>
                  </div>
                  <div className="mt-6 flex items-center justify-between gap-3">
                    <Link
                      to={TUBE_ROUTES[p.id]}
                      data-testid={`product-${p.id}-explore-btn`}
                      className="inline-flex items-center gap-2 text-[13px] font-medium text-[#F5F3EF] hover:text-[#E6CA65] transition-colors"
                    >
                      Explore this tube <ArrowRight size={13} />
                    </Link>
                    <button
                      onClick={() => {
                        window.dispatchEvent(new CustomEvent("ptd:quote-style", { detail: p.id }));
                        document.querySelector("#quote")?.scrollIntoView({ behavior: "smooth" });
                      }}
                      data-testid={`product-${p.id}-quote-btn`}
                      className="inline-flex items-center gap-2 text-[13px] font-medium text-[#D4AF37] hover:text-[#E6CA65] transition-colors"
                    >
                      Quote this style
                    </button>
                  </div>
                </div>
              </article>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);
